import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Navigation from '../components/Navigation'
import PortfolioModeSelector from '../components/investments/PortfolioModeSelector'
import PortfolioPanel from '../components/investments/PortfolioPanel'
import TradingPanel from '../components/investments/TradingPanel'
import OrderHistoryPanel from '../components/investments/OrderHistoryPanel'
import SimplyStockBanner from '../components/investments/SimplyStockBanner'
import { useAuth } from '../hooks/useAuth'
import { useVirtualPortfolio } from '../hooks/useVirtualPortfolio'
import { useUserEngagement } from '../hooks/useUserEngagement'
import { virtualTradingService } from '../services/virtualTradingService'
import { TrendingUp, DollarSign, Clock, Settings, Sparkles } from 'lucide-react'

type TabType = 'portfolio' | 'trade' | 'history'

const InvestmentsPage: React.FC = () => {
  const { user, loading: authLoading } = useAuth()
  const [activeTab, setActiveTab] = useState<TabType>('portfolio')
  const [portfolioInitialized, setPortfolioInitialized] = useState(false)
  const [initializing, setInitializing] = useState(false)

  const {
    portfolio,
    holdings,
    loading: portfolioLoading,
    error: portfolioError,
    refreshPortfolio,
    refreshHoldings,
    totalValue,
    totalGainLoss,
    totalGainLossPercent
  } = useVirtualPortfolio()

  const {
    tradeCount,
    shouldShowSimplyStockBanner,
    dismissSimplyStockBanner,
    incrementTradeCount
  } = useUserEngagement()

  // Check if portfolio exists when component mounts
  useEffect(() => {
    const checkPortfolio = async () => {
      if (!user || authLoading) return

      try {
        const existingPortfolio = await virtualTradingService.getPortfolio(user.id)
        setPortfolioInitialized(!!existingPortfolio)
      } catch (error) {
        console.error('Error checking portfolio:', error)
        setPortfolioInitialized(false)
      }
    }

    checkPortfolio()
  }, [user, authLoading])

  const handlePortfolioModeSelect = async (mode: 'beginner' | 'intermediate' | 'advanced') => {
    if (!user) return

    try {
      setInitializing(true)
      await virtualTradingService.initializePortfolio(user.id, mode)
      setPortfolioInitialized(true)
      await refreshPortfolio()
      await refreshHoldings()
    } catch (error) {
      console.error('Error initializing portfolio:', error)
    } finally {
      setInitializing(false)
    }
  }

  const handleOrderExecuted = () => {
    refreshPortfolio()
    refreshHoldings()
    incrementTradeCount() // Track trade count for SimplyStock banner
  }

  const tabs = [
    { id: 'portfolio' as TabType, label: 'Portfolio', icon: TrendingUp },
    { id: 'trade' as TabType, label: 'Trade', icon: DollarSign },
    { id: 'history' as TabType, label: 'History', icon: Clock }
  ]

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <div className="text-background-400">Loading...</div>
        </div>
      </div>
    )
  }


  // Show portfolio mode selector if portfolio not initialized
  if (!portfolioInitialized) {
    return (
      <div className="min-h-screen bg-background-950">
        <Navigation />
        <PortfolioModeSelector 
          onSelectMode={handlePortfolioModeSelect}
          loading={initializing}
        />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background-950">
      <Navigation />
      
      <div className="container mx-auto px-6 pt-24 pb-10 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-background-100 mb-3">
                Investment Simulator
              </h1>
              <p className="text-background-400 text-lg">
                Practice trading with virtual money and learn investment strategies
              </p>
            </div>
            <div className="flex items-center space-x-6">
              {/* Portfolio Value Display */}
              <div className="text-right">
                <div className="text-3xl font-bold text-background-100">
                  ${totalValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </div>
                <div className={`text-lg font-medium ${
                  totalGainLoss >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {totalGainLoss >= 0 ? '+' : ''}${totalGainLoss.toFixed(2)} ({totalGainLossPercent.toFixed(2)}%)
                </div>
              </div>
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="p-3 hover:bg-background-800/50 rounded-xl transition-all duration-300"
              >
                <Settings className="w-6 h-6 text-background-400" />
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* SimplyStock Banner */}
        {shouldShowSimplyStockBanner && (
          <SimplyStockBanner
            onDismiss={dismissSimplyStockBanner}
            tradeCount={tradeCount}
          />
        )}

        {/* Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex space-x-2 mb-10 bg-background-900/50 backdrop-blur-sm rounded-2xl p-2 border border-background-700/50"
        >
          {tabs.map((tab) => (
            <motion.button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`flex-1 flex items-center justify-center space-x-3 py-4 px-6 rounded-xl text-sm font-semibold transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-primary-500 text-white shadow-lg'
                  : 'text-background-400 hover:text-background-200 hover:bg-background-800/50'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              <span>{tab.label}</span>
            </motion.button>
          ))}
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            {activeTab === 'portfolio' && (
              <PortfolioPanel
                portfolio={portfolio}
                holdings={holdings}
                totalValue={totalValue}
                totalGainLoss={totalGainLoss}
                totalGainLossPercent={totalGainLossPercent}
                loading={portfolioLoading}
                onRefresh={() => {
                  refreshPortfolio()
                  refreshHoldings()
                }}
              />
            )}
            
            {activeTab === 'trade' && (
              <TradingPanel onOrderExecuted={handleOrderExecuted} />
            )}
            
            {activeTab === 'history' && (
              <OrderHistoryPanel onOrderExecuted={handleOrderExecuted} />
            )}
          </motion.div>
        </AnimatePresence>

        {/* Error Display */}
        {portfolioError && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 p-5 bg-red-500/10 border border-red-500/20 rounded-2xl"
          >
            <div className="flex items-center space-x-3">
              <Sparkles className="w-5 h-5 text-red-400" />
              <p className="text-red-400 text-sm font-medium">{portfolioError}</p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  )
}

export default InvestmentsPage

